import max from 'lodash/max';
import {
  onSnapshot,
  firestoreDocumentById,
} from '../firebase/firestore';
import {
  getAnalysesFromIds,
  getOrCreateAnalysisForDocument,
  buildAclObject,
  isAnalysisInGroup,
  isAnalysisInPersonalPortfolio,
} from './analysisHelper';
import { MUTATION_REGISTER_SNAPSHOT_LISTENER } from './constants';
import { types as authentication } from './authentication';

const MODULE_PREFIX = 'portfolio';
const GETTER_GROUPS_PORTFOLIO_ANALYSES = 'groupsPortfolioAnalyses';
const GETTER_PORTFOLIO_ANALYSES = 'portfolioAnalyses';
const GETTER_PORTFOLIO = 'portfolio';
const GETTER_ANALYSIS_IN_GROUP = 'analysisInGroup';
const GETTER_ANALYSIS_IN_PERSONAL_PORTFOLIO = 'analysisInPersonalPortfolio';
const MUTATION_FETCHED_PORTFOLIO_ANALYSES = 'FETCHED_PORTFOLIO_ANALYSES';
const MUTATION_FETCHED_PORTFOLIO = 'FETCHED_PORTFOLIO';
const MUTATION_PORTFOLIO_FETCHED = 'PORTFOLIO_FETCHED';
const MUTATION_USER_PORTFOLIO_FETCHED = 'USER_PORTFOLIO_FETCHED';
const MUTATION_FETCHED_GROUP_PORTFOLIO = 'FETCHED_GROUP_PORTFOLIO';
const MUTATION_FETCHED_GROUPS_PORTFOLIO_ANALYSES = 'FETCHED_GROUPS_PORTFOLIO_ANALYSES';
const MUTATION_SET_GROUP_PORTFOLIO_ANALYSES = 'SET_GROUP_PORTFOLIO_ANALYSES';
const ACTION_FETCH_PORTFOLIO = 'fetchPortfolio';
const ACTION_FETCH_USER_PORTFOLIO = 'fetchUserPortfolio';

const initialState = {
  groupPortfolios: {},
  groupsPortfolioAnalyses: {},
  portfolio: {},
  portfolioFetched: false,
  userPortfolioFetched: false,
  portfolioAnalyses: [],
};

const getters = {
  [GETTER_GROUPS_PORTFOLIO_ANALYSES]: state => state.groupsPortfolioAnalyses,
  [GETTER_PORTFOLIO_ANALYSES]: state => state.portfolioAnalyses,
  [GETTER_PORTFOLIO]: state => state.portfolio,
  [GETTER_ANALYSIS_IN_GROUP]: state => isAnalysisInGroup(state),
  [GETTER_ANALYSIS_IN_PERSONAL_PORTFOLIO]: (state) => isAnalysisInPersonalPortfolio(state),
};

const mutations = {
  [MUTATION_FETCHED_PORTFOLIO_ANALYSES]: (state, portfolioAnalyses) => {
    state.portfolioAnalyses = portfolioAnalyses;
  },
  [MUTATION_FETCHED_PORTFOLIO]: (state, portfolio) => {
    state.portfolio = portfolio;
  },
  [MUTATION_PORTFOLIO_FETCHED]: state => {
    state.portfolioFetched = true;
  },
  [MUTATION_USER_PORTFOLIO_FETCHED]: state => {
    state.userPortfolioFetched = true;
  },
  [MUTATION_FETCHED_GROUP_PORTFOLIO]: (state, { groupId, groupPortfolio }) => {
    state.groupPortfolios = {
      ...state.groupPortfolios,
      [groupId]: groupPortfolio,
    };
  },
  [MUTATION_FETCHED_GROUPS_PORTFOLIO_ANALYSES]: (state, portfolioData) => {
    state.groupsPortfolioAnalyses = {
      ...state.groupsPortfolioAnalyses,
      [portfolioData.group.id]: portfolioData,
    };
  },
  [MUTATION_SET_GROUP_PORTFOLIO_ANALYSES]: (state, groupsPortfolioAnalyses) => {
    state.groupsPortfolioAnalyses = groupsPortfolioAnalyses;
  },
};

function limitNumberAnalyses(maxAnalysesPerSection, analysisIds) {
  if (!maxAnalysesPerSection) return analysisIds;
  if (!analysisIds && analysisIds.length > 0) return analysisIds;

  const start = max([0, analysisIds.length - maxAnalysesPerSection]);
  const end = analysisIds.length;

  return analysisIds.slice(start, end);
}

async function onUserPortfolioSnapshot(commit, rootGetters, userPortfolioSnapshot) {
  const userId = rootGetters[authentication.getters.userId];
  const displayName = rootGetters[authentication.getters.displayName];
  try {
    const userPortfolioSnapshotData = await userPortfolioSnapshot.data();
    commit(MUTATION_FETCHED_PORTFOLIO, userPortfolioSnapshotData);
    let { analysisIds } = userPortfolioSnapshotData;

    const maxPortfolioSectionSize = rootGetters[authentication.getters.maxPortfolioSectionSize];
    analysisIds = limitNumberAnalyses(maxPortfolioSectionSize, analysisIds);

    const analysesData = await getAnalysesFromIds(userId, displayName, analysisIds);
    commit(MUTATION_FETCHED_PORTFOLIO_ANALYSES, analysesData);
  } catch (e) {
    console.error('Error onUserPortfolioSnapshot', e);
    throw e;
  }
}

const onGroupPortfolioSnapshot = (group) => async (commit, rootGetters, groupPortfolioSnapshot) => {
  // const { pagination } = rootGetters;
  const userId = rootGetters[authentication.getters.userId];
  const displayName = rootGetters[authentication.getters.displayName];
  try {
    const groupPortfolioSnapshotData = await groupPortfolioSnapshot.data();

    commit(MUTATION_FETCHED_GROUP_PORTFOLIO, { groupId: group.id, groupPortfolio: groupPortfolioSnapshotData });

    if (groupPortfolioSnapshotData) {
      let { analysisIds } = groupPortfolioSnapshotData;
      if (!analysisIds) return;

      const maxPortfolioSectionSize = rootGetters[authentication.getters.maxPortfolioSectionSize];
      analysisIds = limitNumberAnalyses(maxPortfolioSectionSize, analysisIds);

      const analysesData = await getAnalysesFromIds(userId, displayName, analysisIds);
      commit(MUTATION_FETCHED_GROUPS_PORTFOLIO_ANALYSES, { group, analysesData });
    }
  } catch (e) {
    console.error('Error onGroupPortfolioSnapshot', e);
    throw e;
  }
};

const fetchUserPortfolio = (commit, rootGetters) => {
  const userId = rootGetters[authentication.getters.firebaseUid];
  const query = firestoreDocumentById('userPortfolios', userId);
  const unsubscribePortfolio = onSnapshot(query, onUserPortfolioSnapshot.bind(this, commit, rootGetters));

  commit(
    MUTATION_REGISTER_SNAPSHOT_LISTENER,
    { snapshotListener: unsubscribePortfolio, id: 'fetchUserPortfolio' },
    { root: true },
  );
};

const fetchGroupsPortfolio = (commit, rootGetters) => {
  const groups = rootGetters[authentication.getters.groups];

  groups.forEach((g) => {
    const query = firestoreDocumentById('groupPortfolios', g.id);
    const unsubscribeGroupsPortfolios = onSnapshot(query, onGroupPortfolioSnapshot(g).bind(this, commit, rootGetters));

    commit(
      MUTATION_REGISTER_SNAPSHOT_LISTENER,
      { snapshotListener: unsubscribeGroupsPortfolios, id: `fetchGroupsPortfolio.${g.id}` },
      { root: true },
    );
  });
};

const actions = {
  [ACTION_FETCH_PORTFOLIO]: async ({ commit, state, rootGetters }) => {
    if (!state.portfolioFetched) {
      commit(MUTATION_PORTFOLIO_FETCHED);
      fetchUserPortfolio(commit, rootGetters);
      fetchGroupsPortfolio(commit, rootGetters);
    }
  },
  [ACTION_FETCH_USER_PORTFOLIO]: async ({ commit, state, rootGetters }) => {
    if (!state.portfolioFetched && !state.userPortfolioFetched) {
      commit(MUTATION_USER_PORTFOLIO_FETCHED);
      fetchUserPortfolio(commit, rootGetters);
    }
  },
};

export const getAccessibleAnalysisIdForDocument = async (
  document,
  userId,
  userDisplayName,
  ownerType,
  ownerId,
  ownerName,
) => {
  const isDocument = document.documentDetails.documentType === 'document';
  const aclObject = buildAclObject(ownerType, ownerId, ownerName);

  let analysisId;

  // This is such an ugly hack
  if (isDocument) {
    const { analysis } = await getOrCreateAnalysisForDocument(
      document, userId, userDisplayName, aclObject,
    );
    analysisId = analysis.id;
  } else {
    analysisId = document.id;
  }

  return analysisId;
};


export default {
  namespaced: true,
  state: initialState,
  getters,
  mutations,
  actions,
};

export const types = {
  getters: {
    analysisInGroup: `${MODULE_PREFIX}/${GETTER_ANALYSIS_IN_GROUP}`,
    analysisInPersonalPortfolio: `${MODULE_PREFIX}/${GETTER_ANALYSIS_IN_PERSONAL_PORTFOLIO}`,
    groupsPortfolioAnalyses: `${MODULE_PREFIX}/${GETTER_GROUPS_PORTFOLIO_ANALYSES}`, // TODO should probably not point to user
    portfolio: `${MODULE_PREFIX}/${GETTER_PORTFOLIO}`,
    portfolioAnalyses: `${MODULE_PREFIX}/${GETTER_PORTFOLIO_ANALYSES}`,
  },
  mutations: {
    FETCHED_PORTFOLIO_ANALYSES: `${MODULE_PREFIX}/${MUTATION_FETCHED_PORTFOLIO_ANALYSES}`,
  },
  actions: {
    fetchPortfolio: `${MODULE_PREFIX}/${ACTION_FETCH_PORTFOLIO}`,
    fetchUserPortfolio: `${MODULE_PREFIX}/${ACTION_FETCH_USER_PORTFOLIO}`,
  },
};
